/**
 * Post-login return path — keeps the deep link through the OIDC hand-off.
 *
 * THE DEFECT
 *
 * A fresh load of /coaching (or any protected route) went AuthGuard → /login →
 * Central → /auth/callback and always ended on /dashboard. The intended path
 * WAS stored before the redirect (OAUTH_RETURN_PATH_KEY) and read back by the
 * callback — but the callback only rewrote the address bar with
 * history.replaceState, which React Router never sees. LoginPage then
 * navigated to its own `from`, which after a full-page redirect has no router
 * state and fell back to /dashboard.
 *
 * THE FIX
 *
 * The callback hands the stored path to LoginPage through this module; the
 * path is validated on the way in AND on the way out.
 *
 * OPEN-REDIRECT SAFETY
 *
 * Only a same-origin, relative path is ever returned: it must start with a
 * single "/", contain no backslash or control character, resolve to this
 * origin, and not be a login/callback route (which would loop). Anything else
 * — "//evil.example", "/\\evil.example", "https://…", "javascript:…" — is
 * refused and the caller falls back to /dashboard.
 */

export const DEFAULT_POST_LOGIN_PATH = '/dashboard';

const MAX_RETURN_PATH_LENGTH = 2048;
const LOOP_PATHS = new Set(['/login', '/auth/callback']);

function currentOrigin(): string {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'http://localhost';
}

/**
 * The path itself (pathname + search + hash) when `raw` is a safe same-origin
 * relative path; otherwise null.
 */
export function sanitizeReturnPath(raw: unknown, origin: string = currentOrigin()): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > MAX_RETURN_PATH_LENGTH) return null;
  // Exactly one leading slash: "//host" and "/\host" are protocol-relative.
  if (raw[0] !== '/' || raw[1] === '/' || raw[1] === '\\') return null;
  // Browsers treat "\" as "/" in URLs, and strip tabs/newlines: refuse both.
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return null;

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== new URL(origin).origin) return null;
  if (LOOP_PATHS.has(url.pathname)) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}

// ── Hand-off from the OIDC callback to LoginPage (same page load) ──────────

let pendingPostLoginPath: string | null = null;

/** Called by the OIDC callback with the path stored before the redirect. */
export function setPostLoginPath(raw: unknown): void {
  pendingPostLoginPath = sanitizeReturnPath(raw);
}

/** The validated path the callback recorded, if any (does not clear it). */
export function peekPostLoginPath(): string | null {
  return pendingPostLoginPath;
}

/** Clear after navigating, so a later login does not reuse it. */
export function clearPostLoginPath(): void {
  pendingPostLoginPath = null;
}

/**
 * Where LoginPage should send an authenticated user, in priority order:
 *   1. the path the OIDC callback restored (the original deep link),
 *   2. AuthGuard's in-app router state (no full-page redirect happened),
 *   3. an explicit `?returnTo=` (BillingPage, PricingPage, … link to it),
 *   4. /dashboard.
 * Every candidate passes sanitizeReturnPath.
 */
export function resolvePostLoginPath(input: {
  callbackPath?: string | null;
  routerFrom?: { pathname?: string; search?: string; hash?: string } | null;
  returnToParam?: string | null;
  origin?: string;
}): string {
  const origin = input.origin ?? currentOrigin();
  const fromState = input.routerFrom?.pathname
    ? `${input.routerFrom.pathname}${input.routerFrom.search ?? ''}${input.routerFrom.hash ?? ''}`
    : null;
  return (
    sanitizeReturnPath(input.callbackPath, origin) ??
    sanitizeReturnPath(fromState, origin) ??
    sanitizeReturnPath(input.returnToParam, origin) ??
    DEFAULT_POST_LOGIN_PATH
  );
}
